import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  HeartPulse,
  Brain,
  PhoneCall,
  Clock,
  ArrowRight,
  ShieldAlert,
  CheckCircle2,
  HelpCircle,
  Stethoscope,
} from "lucide-react";

export default function HowToGetMentalHealthHelpPage() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/how-to-get-mental-health-help.jpg";

  const faqs = [
    {
      q: "Where should I start if I don't know what kind of mental health care I need?",
      a: "Starting with a comprehensive psychiatric evaluation or speaking with a licensed therapist is the best initial step. A professional can assess your symptoms and guide you toward appropriate therapy, medication, or interventional options.",
    },
    {
      q: "What should I do if I am in immediate crisis?",
      a: "If you or someone you know is in immediate crisis, call or text 988 to reach the Suicide & Crisis Lifeline, available 24/7. In emergency situations, visit the nearest emergency room or call 911.",
    },
    {
      q: "How do I know if my insurance covers mental health treatment?",
      a: "Most major health insurance plans cover psychiatric consultations, therapy, and FDA-cleared treatments like TMS and SPRAVATO®. Our billing specialists in Coral Springs can verify your insurance benefits directly.",
    },
    {
      q: "What non-medication options exist for treatment-resistant mental health conditions?",
      a: "Advanced non-medication options include Transcranial Magnetic Stimulation (TMS), SAINT® Accelerated TMS, SPRAVATO® esketamine nasal spray, and Ketamine therapy.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How to Get Mental Health Help: Steps, Resources & Care | Neuro Wellness"
        description="Learn how to seek mental health help, understand care options, verify insurance, and access evidence-based therapy and interventional treatments in Coral Springs, FL."
        canonical="/how-to-get-mental-health-help"
        keywords="how to get mental health help, find mental health care, mental health resources South Florida, psychiatric evaluation Coral Springs, crisis support 988"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-to-get-mental-health-help#webpage",
                name: "How to Get Mental Health Help: A Practical Step-by-Step Guide",
                url: "https://neurowellnesstms.com/how-to-get-mental-health-help",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Practical step-by-step guidance on reaching out for mental health support, finding qualified doctors and therapists, and exploring modern treatments.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-to-get-mental-health-help#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <HeartPulse className="h-4 w-4" />
                    Mental Health Guidance
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How to Get Mental Health Help:{" "}
                  <span className="text-accent">Where to Begin</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Taking the first step toward mental wellness can feel
                  overwhelming. Discover clear, compassionate guidance on
                  choosing care providers, navigating insurance, and finding
                  effective treatment.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Brain className="h-4 w-4 text-accent" />
                    <span>Evidence-Based Treatment</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Stethoscope className="h-4 w-4 text-accent" />
                    <span>In-Network Insurance Verification</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      Confidential Outreach
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Request Mental Health Assessment
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Connect directly with our clinical intake specialists in
                      Coral Springs, FL.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="how-to-get-mental-health-help"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "How to Get Mental Health Help",
                href: "/how-to-get-mental-health-help",
              },
            ]}
          />
        </div>

        {/* 988 CRISIS BANNER */}
        <section className="container-px mx-auto max-w-4xl py-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 sm:p-5 flex items-start gap-3 text-red-900">
            <ShieldAlert className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <span className="font-bold">Need Immediate Help? </span>
              If you or a loved one are in immediate danger or experiencing
              severe emotional distress, please call or text{" "}
              <span className="font-bold text-red-700">988</span> for the
              Suicide & Crisis Lifeline (24/7 free & confidential), or call{" "}
              <span className="font-bold">911</span>.
            </div>
          </div>
        </section>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Step-by-Step Guide to Seeking Help
            </h2>
            <div className="space-y-4">
              <div className="bg-card border p-5 rounded-xl space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <span className="bg-accent/20 text-accent rounded-full h-7 w-7 flex items-center justify-center text-sm font-extrabold">
                    1
                  </span>
                  Recognize Symptoms & Needs
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Acknowledge how symptoms are affecting daily routine, work,
                  relationships, or physical energy. Common signs include
                  lingering sadness, anxiety, changes in sleep, or inability to
                  enjoy life.
                </p>
              </div>

              <div className="bg-card border p-5 rounded-xl space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <span className="bg-accent/20 text-accent rounded-full h-7 w-7 flex items-center justify-center text-sm font-extrabold">
                    2
                  </span>
                  Consult a Medical or Mental Health Professional
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Reach out to a primary care doctor, board-certified
                  psychiatrist, or licensed psychotherapist. An initial
                  assessment helps determine whether therapy, medication, or
                  specialized treatments are required.
                </p>
              </div>

              <div className="bg-card border p-5 rounded-xl space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <span className="bg-accent/20 text-accent rounded-full h-7 w-7 flex items-center justify-center text-sm font-extrabold">
                    3
                  </span>
                  Explore Advanced Interventional Care
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  For individuals who haven't found adequate relief from
                  standard medications or therapy, advanced non-invasive options
                  such as TMS, SAINT® TMS, and SPRAVATO® offer innovative
                  pathways to recovery.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Types of Mental Health Care Providers
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3 pt-2 text-sm text-foreground">
              {[
                "Psychiatrists (MD/DO): Diagnosis & medication management",
                "Psychologists (PhD/PsyD): Psychotherapeutic assessment & therapy",
                "Licensed Therapists (LCSW, LMHC): Individual & family talk therapy",
                "Interventional Centers: Advanced procedures (TMS, SPRAVATO®)",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 bg-muted/30 p-3 rounded-lg border"
                >
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Related Resources
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/how-to-see-a-psychiatrist"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    How To See a Psychiatrist
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Step-by-step guide to psychiatric evaluation
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
              <Link
                to="/check-insurance-options"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    Check Insurance Coverage
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Verify your mental health benefits in Coral Springs
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              We Are Here to Help You Navigate Next Steps
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              Our compassionate intake team in Coral Springs, FL is available to
              answer questions, verify insurance benefits, and schedule your
              clinical consultation.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
